import React from 'react';
import { Box, Typography } from '@mui/material';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import {
    brandAwardBodyTextSx,
    brandAwardEmptyStateSx,
    editorialHeadlineSx,
    editorialLabelSx,
    midnight,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';

export type ContestTabVisualVariant = 'default' | 'brand';

interface ContestAnalyticsTabProps {
    visualVariant?: ContestTabVisualVariant;
}

export const ContestAnalyticsTab: React.FC<ContestAnalyticsTabProps> = ({ visualVariant = 'default' }) => {
    const { themeStyle } = useThemeContext();
    const isBrand = visualVariant === 'brand';

    if (isBrand) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                    <Typography
                        sx={{
                            ...editorialHeadlineSx,
                            fontSize: { xs: 22, sm: 26 },
                            color: midnight.onSurface,
                        }}>
                        Analytics
                    </Typography>
                    <Typography sx={{ ...editorialLabelSx, mt: 0.75, letterSpacing: '0.12em' }}>
                        Performance insights — coming soon
                    </Typography>
                </Box>
                <Box sx={brandAwardEmptyStateSx}>
                    <BarChartOutlinedIcon
                        sx={{ fontSize: 48, color: midnight.primary, mb: 2, opacity: 0.9 }}
                    />
                    <Typography
                        sx={{
                            ...editorialHeadlineSx,
                            fontSize: 20,
                            color: midnight.onSurface,
                            mb: 1,
                        }}>
                        Campaign analytics coming soon
                    </Typography>
                    <Typography sx={{ ...brandAwardBodyTextSx, maxWidth: 480, mx: 'auto' }}>
                        We&apos;re building engagement, sales, and revenue views for your campaign. This workspace
                        will surface them here when ready.
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1.25rem',
                    color: theme[themeStyle].primary_white,
                    mb: 3,
                }}>
                Event Analytics
            </Typography>

            <Box
                sx={{
                    backgroundColor: theme[themeStyle].secondaryBg,
                    borderRadius: 2,
                    textAlign: 'center',
                    py: 6,
                    px: 3,
                }}>
                <BarChartOutlinedIcon
                    sx={{ fontSize: 64, color: theme[themeStyle].primary_white, mb: 2 }}
                />
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.5rem',
                        color: theme[themeStyle].primary_white,
                        mb: 1,
                    }}>
                    Campaign Analytics Coming Soon
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '1rem',
                        color: theme[themeStyle].light_gray_text,
                        maxWidth: 500,
                        mx: 'auto',
                    }}>
                    We&apos;re working on advanced analytics for your events. Soon you&apos;ll be able to track
                    engagement, sales, revenue metrics, and more in this section.
                </Typography>
            </Box>
        </Box>
    );
};
